import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
    BIDIRECTIONAL_PANEL_COLORS,
    BidirectionalPanel,
    BidirectionalPanelConfig,
    Config,
    DEFAULT_VOICE_INDEX,
    DeviceConfig,
    Input,
    Language,
    Output,
    Recognition,
    RecognizableAndTranslatableLanguage,
    SERVER_URL,
    SESSION_URL,
    SessionData,
    SessionStates,
    SpeechTokenData,
    TranslatableAndSpeakableLanguage,
    TranslationResult,
    TranslationsBody
} from '../contexts/Configuration';
import {DeviceType} from '../components/Controler';
import {getSessionContext} from '../contexts/SessionResolver';
import {UUID} from 'crypto';
import {RosettaClient} from '../api/RosettaClient';
import MediaDevicesUtils from "../utils/MediaDevicesUtils";
import {applyLiveOutputVolume, isOutputDeviceActive} from "../utils/OutputVolume";

// const MAX_HISTORY_DURATION_IN_MILLIS: number = 1000 * 30;//30 seconds
const MAX_HISTORY_DURATION_IN_MILLIS: number = 1000 * 60 * 4;//4 minutos

type InitialState = {
    translationApiKey: string | null,
    speechApiKey: string | null,

    input: Input | null,
    outputs: Output[],

    inputGain: number,
    outputGains: { [key: string]: number },

    preSelectedInputLanguage: string | null,
    preSelectedOutputLanguages: { [key: string]: string },

    preSelectedOutputTextOnly: { [key: string]: boolean },
    preSelectedOutputVoices: { [key: string]: string },

    outputsPace: number,
    autoDetectLang: boolean,
    sessionDurationInMins: number,
    broadcastEnabled: boolean,

    modelType: string,
    useDictionary: boolean,
    disabledGlossaryIds: string[],

    alreadySelected: string[],
    transcriptionPanels: { id: string, isExtra: boolean }[],
    canStartSession: boolean,
    sessionState: SessionStates,
    currentSessionId: UUID | null,
    currentSessionAPIToken: string | null,
    recognizedTexts: Recognition[],
    currentRecognition: string | null,
    recognizedTextsByPanel: { [panelId: string]: Recognition[] },
    currentRecognitionByPanel: { [panelId: string]: string | null },
    panelTextHistory: { [panelId: string]: { timestamp: number, text: string }[] },
    partialTextByPanel: { [panelId: string]: string | null },
    currentTranslations: Map<string, string>,
    translations: Map<string, { timestamp: number, text: string }[]>,
    sessionStarted: boolean,
    availableOutputDevicesAmount: number,
    onTestMode: boolean,

    isTwoWayMode: boolean,
    bidirectionalPanels: BidirectionalPanel[],
    activeInputPanelId: string | null,
    floorFeedEnabled: boolean,
}

function loadBidirectionalPanelsFromCache(cfg: Config): BidirectionalPanel[] {
    const panelsConfig = cfg.bidirectionalPanelsConfig || {};
    const ids = Object.keys(panelsConfig);
    if (ids.length === 0) {
        return [
            {id: 'bipanel_0', language: null, inputDevice: null, outputDevice: null, inputGain: 0.5, outputGain: 1.0, voiceName: null, textOnly: false, outputOnly: false, inputOnly: false, color: BIDIRECTIONAL_PANEL_COLORS[0]},
            {id: 'bipanel_1', language: null, inputDevice: null, outputDevice: null, inputGain: 0.5, outputGain: 1.0, voiceName: null, textOnly: false, outputOnly: false, inputOnly: false, color: BIDIRECTIONAL_PANEL_COLORS[1]},
        ];
    }
    return ids.map((panelId, idx) => {
        const p = panelsConfig[panelId] as BidirectionalPanel;
        return {
            id: panelId,
            language: null,
            inputDevice: null,
            outputDevice: null,
            inputGain: p.inputGain ?? 0.5,
            outputGain: p.outputGain ?? 1.0,
            voiceName: p.voiceName ?? null,
            textOnly: p.textOnly ?? false,
            outputOnly: p.outputOnly ?? false,
            inputOnly: p.inputOnly ?? false,
            color: p.color ?? BIDIRECTIONAL_PANEL_COLORS[idx % BIDIRECTIONAL_PANEL_COLORS.length],
        } as BidirectionalPanel;
    });
}

function getPanelIdsFromCache(cfg: Config): string[] {
    const all = new Set<string>();
    // Include all possible sources of panel IDs to ensure panels with partial configs are restored
    Object.keys(cfg.outputLanguages || {}).forEach(k => all.add(k));
    Object.keys(cfg.outputDevicesConfig || {}).filter(k => !k.startsWith('bipanel_')).forEach(k => all.add(k));
    Object.keys(cfg.outputGains || {}).forEach(k => all.add(k));
    Object.keys(cfg.outputTextOnly || {}).forEach(k => all.add(k));
    const ids = Array.from(all);
    return ids.length ? ids : ['panel_1'];
}

const initialState: InitialState = {
    translationApiKey: null,
    speechApiKey: null,

    input: null,
    outputs: [],

    inputGain: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).inputGain || 0.5,
    outputGains: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).outputGains || {},

    preSelectedInputLanguage: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).inputLanguage || null,
    preSelectedOutputLanguages: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).outputLanguages || {},

    preSelectedOutputTextOnly: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).outputTextOnly || {},
    preSelectedOutputVoices: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).outputVoices || {},

    outputsPace: 1.20,
    autoDetectLang: false,
    sessionDurationInMins: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).sessionDuration || 60 * 2, //default 2hs
    broadcastEnabled: false,

    modelType: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).modelType || 'onlyStableModel',
    useDictionary: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).useDictionary || false,
    disabledGlossaryIds: (() => {
        const value = (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).disabledGlossaryIds;
        return Array.isArray(value)
            ? value.filter((id): id is string => typeof id === 'string')
            : [];
    })(),


    alreadySelected: [],

    // transcriptionPanels: [{id: 'panel_1', isExtra: false}],
    // transcriptionPanels: Object.keys(
    //     (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).outputLanguages || {'panel_1': ''}
    // )
    transcriptionPanels: getPanelIdsFromCache((JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config))
        .map(panelId => ({id: panelId, isExtra: panelId !== 'panel_1'})),

    canStartSession: false,//input and at last 1 output
    sessionStarted: false, //flag
    sessionState: SessionStates.LOADING,//state
    currentSessionId: null,
    currentSessionAPIToken: null,

    recognizedTexts: [],
    currentRecognition: null,
    recognizedTextsByPanel: {},
    currentRecognitionByPanel: {},
    panelTextHistory: {},
    partialTextByPanel: {},
    currentTranslations: new Map<string, string>(),
    translations: new Map<string, { timestamp: number, text: string }[]>(),

    availableOutputDevicesAmount: 0,

    onTestMode: true,

    isTwoWayMode: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).isTwoWayMode ?? true,
    bidirectionalPanels: loadBidirectionalPanelsFromCache(JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config),
    activeInputPanelId: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).activeInputPanelId ?? 'bipanel_0',
    floorFeedEnabled: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).floorFeedEnabled ?? false,
}

export type SessionStartedResponse =
    | (Omit<Response, 'json'> & {
    status: number;
    json: () => SessionData | PromiseLike<SessionData>;
});

export type SpeechTokenRefreshResponse =
    | (Omit<Response, 'json'> & {
    status: number;
    json: () => SpeechTokenData | PromiseLike<SpeechTokenData>;
});


export const validateAndStartSession = createAsyncThunk<
    SessionData,
    { input: Input, outputs: Output[], sessionDurationInMins: number, broadcastEnabled: boolean, token: string,
      sessionType?: 'standard' | 'twoWay',
        bidirectionalPanels?: { languageName: string; languageKey: string; color: string; activePanel?: boolean;
            inputDevice: { deviceId: string; kind: string; label?: string; groupId?: string } | null;
            outputDevice: { deviceId: string; kind: string; label?: string; groupId?: string } | null; textOnly: boolean }[] },
    { rejectValue: string }
>(
    'session/start',
    async (params, {rejectWithValue}) => {
        return await RosettaClient.startSession(params.input, params.outputs, params.sessionDurationInMins, params.broadcastEnabled, params.token,
            params.sessionType, params.bidirectionalPanels)
            .catch(error => {
                return rejectWithValue(error.message);
            });
    }
)

export const stopCurrentSession = createAsyncThunk<
    {},
    { token: string, sessionId: UUID | null, reason: string },
    { rejectValue: string }
>(
    'session/stop',
    async (params, {rejectWithValue}) => {
        // console.log('[stopCurrentSession]', params)
        if (params.sessionId === null) {
            console.log('[stopCurrentSession] Error ACA', params)
            return rejectWithValue('Unknown required data of on process session')
        }

        return await fetch(`${SERVER_URL}${SESSION_URL}/${params.sessionId}`, {
            method: 'PATCH',
            headers: {
                'content-type': 'application/json',
                Authorization: `Bearer ${params.token}`
            },
            body: JSON.stringify({reason: params.reason})
        })
            .then(() => {
                return {};
            })
            .catch(error => {
                return rejectWithValue(error.message)
            })
    }
)

export const sendTranslations = createAsyncThunk<
    {},
    { token: string, sessionId: UUID | null, translations: TranslationsBody },
    { rejectValue: string }
>(
    'session/translations',
    async (params, {rejectWithValue}) => {
        if (params.sessionId === null) {
            console.log('[sendTranslations] Error ACA', params)
            return rejectWithValue('Missing required data of on process session');
        }

        if (!params.translations) {
            return rejectWithValue('Token not available');
        }

        return await fetch(`${SERVER_URL}${SESSION_URL}/${params.sessionId}/translation`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                Authorization: `Bearer ${params.token}`
            },
            body: JSON.stringify(params.translations)
        }).then(response => {
            if (response.ok) {
                console.log('SENT OK!')
                return {};
            } else {
                console.log('SENT FAIL to sent translations to server:', response.json())
                throw new Error('FAIL!!!!!!!')
            }
        }).catch(error => {
            console.log('SENT FAIL to sent translations to server!', error)
            return rejectWithValue(error.message)
        })
    }
)

export const updateSession = createAsyncThunk<
    {},
    { token: string, sessionId: UUID, config: { duration?: number } },
    { rejectValue: string }
>(
    'session/update',
    async (params, {rejectWithValue}) => {
        if (params.sessionId === null) {
            console.log('[updateSession] Error ACA', params)
            return rejectWithValue('Missing required data of on process session');
        }

        return await fetch(`${SERVER_URL}${SESSION_URL}/${params.sessionId}/config`, {
            method: 'PATCH',
            headers: {
                'content-type': 'application/json',
                Authorization: `Bearer ${params.token}`
            },
            body: JSON.stringify(params.config)
        }).then(response => {
            if (response.ok) {
                console.log('SENT OK!')
                return {};
            } else {
                console.log('FAIL on session config update:', response.json())
                throw new Error('FAIL!!!!!!!')
            }
        }).catch(error => {
            console.log('FAIL on session config update:', error)
            return rejectWithValue(error.message)
        })
    }
)


function saveBiPanelConfig(state: InitialState) {
    const cfg = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
    const panelsConfig: { [id: string]: BidirectionalPanelConfig } = {};
    state.bidirectionalPanels.forEach(p => {
        panelsConfig[p.id] = {
            languageKey: p.language?.recognitionKey,
            inputGain: p.inputGain,
            outputGain: p.outputGain,
            voiceName: p.voiceName ?? undefined,
            textOnly: p.textOnly,
            outputOnly: p.outputOnly ?? false,
            inputOnly: p.inputOnly ?? false,
            color: p.color,
        };
    });
    localStorage.setItem('preSelectedConfig', JSON.stringify({
        ...cfg,
        bidirectionalPanelsConfig: panelsConfig,
        activeInputPanelId: state.activeInputPanelId,
    }));
}

function recomputeInputOutputs(state: InitialState) {
    if (!state.isTwoWayMode) return;

    const activePanel = state.bidirectionalPanels.find(p => p.id === state.activeInputPanelId);
    if (!activePanel || !activePanel.language || !activePanel.inputDevice) {
        state.input = null;
        state.outputs = [];
        return;
    }

    state.input = {
        id: activePanel.id,
        device: activePanel.inputDevice,
        language: {name: activePanel.language.name, recognitionKey: activePanel.language.recognitionKey},
        gain: activePanel.inputGain,
        configComplete: true,
        autoDetect: false,
        translationKey: activePanel.language.translationKey,
    } as Input;

    // Match the session-start output filter (TwoWay.tsx): a panel counts as an output
    // when it has a language AND either an output device OR is text-only. Text-only panels
    // legitimately have no output device but are still billed (TEXT_ONLY_TRANSLATION), so
    // they must be present here for the balance/minutes estimate to stay coherent.
    state.outputs = state.bidirectionalPanels
        .filter(p => p.id !== state.activeInputPanelId && p.language && (p.outputDevice || p.textOnly))
        .map(p => ({
            id: p.id,
            device: p.outputDevice,
            language: {
                name: p.language!.name,
                speakKey: p.language!.speakKey,
                translationKey: p.language!.translationKey,
                voices: p.language!.voices,
            },
            gain: p.outputGain,
            configComplete: true,
            isExtra: true,
            pace: state.outputsPace,
            textOnly: p.textOnly,
            voiceIndex: p.voiceName ? (p.language!.voices.findIndex(v => v.name === p.voiceName) ?? 0) : 0,
            sessionDuration: state.sessionDurationInMins,
        } as Output));
}

const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        setApiKey(state, action: PayloadAction<{ translationKey: string | null, speechKey: string | null }>) {
            state.translationApiKey = action.payload.translationKey;
            state.speechApiKey = action.payload.speechKey;

            // console.log('SETTEADAS API KEYS');
            // if (action.payload) {
            //     localStorage.setItem('apiKey', action.payload);
            // } else {
            //     localStorage.removeItem('apiKey');
            // }
        },
        setInitialOutputDevicesAmount(state, action: PayloadAction<number>) {
            state.availableOutputDevicesAmount = action.payload;
        },
        updateCanStartSession(state, action: PayloadAction<boolean>) {
            state.canStartSession = action.payload;
        },
        updateSessionState(state, action: PayloadAction<{ state: SessionStates }>) {
            // if (state.canStartSession) {
            state.sessionState = action.payload.state;
            // }

            const isStarting = state.sessionState === SessionStates.ON;
            if (isStarting) {
                state.recognizedTexts = [];
                state.currentRecognition = null;
                state.recognizedTextsByPanel = {};
                state.currentRecognitionByPanel = {};
                state.panelTextHistory = {};
                state.partialTextByPanel = {};
                state.translations = new Map<string, { timestamp: number, text: string }[]>();
                state.currentTranslations = new Map<string, string>();
            }
            // console.log('---> sessionStarted: ', isStarting)
            state.sessionStarted = isStarting;
        },

        addPanel(state, action: PayloadAction<string>) {
            state.transcriptionPanels.push({id: action.payload, isExtra: true});

        },
        removePanel(state, action: PayloadAction<string>) {
            const panelId = action.payload;

            state.outputs = state.outputs.filter(output => panelId !== output.id);
            state.transcriptionPanels = state.transcriptionPanels.filter(panel => panelId !== panel.id);

            let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

            //UPDATING CONFIG
            const preSelectedOutputLanguages = config.outputLanguages || {};
            delete preSelectedOutputLanguages[panelId];
            // const preSelectedOutputDevices = config.outputDevices || {};
            // delete preSelectedOutputDevices[panelId];
            const preSelectedOutputTextOnly = config.outputTextOnly || {};
            delete preSelectedOutputTextOnly[panelId];
            const preSelectedOutputVoices = config.outputVoices || {};
            delete preSelectedOutputVoices[panelId];
            const outputGains = config.outputGains || {};
            delete outputGains[panelId];

            config = {
                ...config,
                outputLanguages: preSelectedOutputLanguages,
                // outputDevices: preSelectedOutputDevices
                outputTextOnly: preSelectedOutputTextOnly,
                outputVoices: preSelectedOutputVoices,
                outputGains: outputGains
            };
            localStorage.setItem('preSelectedConfig', JSON.stringify(config));
            MediaDevicesUtils.instance().forgetSelectedOutputDevice(panelId);

            //UPDATING particular values
            state.preSelectedOutputLanguages = preSelectedOutputLanguages;
            state.preSelectedOutputTextOnly = preSelectedOutputTextOnly;
            state.preSelectedOutputVoices = preSelectedOutputVoices;
            state.outputGains = outputGains;
        },

        updatePanelConfiguration(state, action: PayloadAction<DeviceConfig>) {
            const panelId = action.payload.panelId;
            const type = action.payload.type;

            const device = action.payload.device;
            const language = action.payload.language;
            // Default: 0.5 (50%) for input, 1.0 (100%) for output
            const defaultGain = type === DeviceType.IN ? 0.5 : 1.0;
            const gain = action.payload.gain === undefined || action.payload.gain === null ? defaultGain : action.payload.gain;
            const textOnly = action.payload.textOnly;

            if (type === DeviceType.IN) {
                const lang = language as RecognizableAndTranslatableLanguage;
                const autoDetectEnabled = action.payload.autoDetectLang || false;
                state.input = {
                    id: panelId,
                    device: device,
                    language: language,
                    gain: gain,
                    configComplete: !!(device && (language || autoDetectEnabled)),
                    autoDetect: autoDetectEnabled
                } as Input;

                if (device?.deviceId) {
                    try {
                        // getSessionContext()?.getText2Speech()?.getAudioPlayer()?.setVolume(gain);
                        MediaDevicesUtils.instance().getAudioInputHandler(device?.deviceId)?.setVolume(gain);
                    } catch (error) {

                    }
                }

                /// UPDATING INPUT GAIN
                if (gain) {
                    //UPDATING CONFIG
                    let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
                    config = {...config, inputGain: gain};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(config));

                    //UPDATING particular value
                    state.inputGain = gain;
                } else {
                    //UPDATING particular value
                    state.inputGain = 0.5;
                }

                /// UPDATING INPUT LANG
                if (lang?.recognitionKey) { //Siempre guardo el ultimo elegido
                    const selected = language as RecognizableAndTranslatableLanguage;
                    const preSelectedInputLanguageId = /*selected.translationKey + ':' +*/ selected.recognitionKey;

                    //UPDATING CONFIG
                    let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
                    config = {...config, inputLanguage: preSelectedInputLanguageId};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(config));

                    //UPDATING particular value
                    state.preSelectedInputLanguage = preSelectedInputLanguageId;
                } else {
                    //UPDATING particular value
                    state.preSelectedInputLanguage = null;
                }
            }

            //// ======================================================================================================

            if (type === DeviceType.OUT && !!panelId) {
                const isBiPanel = panelId.startsWith('bipanel_');
                let filtered = state.outputs.filter(output => {
                    // if (output.id === panelId && output.language && !language) {
                    //     state.alreadySelected = state.alreadySelected.filter(l => l !== output.language.translationKey)
                    // }
                    // Drop stale outputs from the OTHER UI namespace. `state.outputs` is shared
                    // between Dashboard (`panel_*`) and TwoWay (`bipanel_*`); after switching
                    // modes, foreign-namespace entries can linger here and inflate the output
                    // count (wrong available-minutes math, leaked devices). Keep only same-namespace.
                    if (output.id.startsWith('bipanel_') !== isBiPanel) return false;
                    return output.id !== panelId
                });
                const lang = language as TranslatableAndSpeakableLanguage;
                const voiceName = action.payload.voiceName;
                const voiceIdx = voiceName ? lang?.voices?.findIndex(v => v.name === voiceName) : -1;
                const resolvedVoiceIndex = voiceIdx >= 0 ? voiceIdx : DEFAULT_VOICE_INDEX;
                filtered.push({
                    id: panelId,
                    device: device,
                    language: language,
                    gain: gain,
                    pace: state.outputsPace,
                    configComplete: !!(device && language),
                    isExtra: panelId !== 'panel_1',
                    textOnly: textOnly,
                    voiceIndex: resolvedVoiceIndex,
                    sessionDuration: state.sessionDurationInMins
                } as Output);
                state.outputs = filtered;


                /// UPDATING OUTPUT GAIN
                // Use explicit null/undefined check so gain=0 (mute) is stored correctly.
                if (gain !== null && gain !== undefined) {
                    //UPDATING CONFIG
                    let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
                    const outputGains = config.outputGains || {};
                    outputGains[panelId] = gain;
                    config = {...config, outputGains: outputGains};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(config));

                    //UPDATING particular value
                    state.outputGains = outputGains;
                }

                //UPDATING TEXT ONLY
                if (textOnly) {
                    let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
                    const preSelectedOutputTextOnly = config.outputTextOnly || {};
                    preSelectedOutputTextOnly[panelId] = textOnly || false;
                    config = {...config, outputTextOnly: preSelectedOutputTextOnly};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(config));

                    //UPDATING particular value
                    state.preSelectedOutputTextOnly = preSelectedOutputTextOnly;
                } else {
                    let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
                    const preSelectedOutputTextOnly = config.outputTextOnly || {};
                    delete preSelectedOutputTextOnly[panelId];
                    config = {...config, outputTextOnly: preSelectedOutputTextOnly};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(config));

                    //UPDATING particular value
                    state.preSelectedOutputTextOnly = preSelectedOutputTextOnly;
                }

                //UPDATING OUTPUT VOICE
                if (voiceName) {
                    let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
                    const outputVoices = config.outputVoices || {};
                    outputVoices[panelId] = voiceName;
                    config = {...config, outputVoices: outputVoices};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(config));

                    state.preSelectedOutputVoices = outputVoices;
                } else {
                    let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
                    const outputVoices = config.outputVoices || {};
                    delete outputVoices[panelId];
                    config = {...config, outputVoices: outputVoices};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(config));

                    state.preSelectedOutputVoices = outputVoices;
                }

                //// UPDATING OUTPUT LANG
                if (lang?.translationKey) {
                    // Keep the user's saved gain, but mute live playback while the
                    // selected output is unavailable so Chrome/Windows cannot route
                    // this language to the default speaker.
                    applyLiveOutputVolume(lang.translationKey, device, gain);

                    const selected = language as TranslatableAndSpeakableLanguage;
                    const preSelectedOutputLanguageId = selected.translationKey + ':' + selected.speakKey;

                    //UPDATING CONFIG
                    let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
                    const preSelectedOutputLanguages = config.outputLanguages || {};
                    // delete preSelectedOutputLanguages[panelId]; //first delete
                    preSelectedOutputLanguages[panelId] = preSelectedOutputLanguageId;
                    config = {...config, outputLanguages: preSelectedOutputLanguages};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(config));

                    //UPDATING particular value
                    state.preSelectedOutputLanguages = preSelectedOutputLanguages;

                } else {
                    //UPDATING CONFIG
                    let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
                    const preSelectedOutputLanguages = config.outputLanguages || {};
                    delete preSelectedOutputLanguages[panelId]; //delete
                    config = {...config, outputLanguages: preSelectedOutputLanguages};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(config));

                    //UPDATING particular value
                    state.preSelectedOutputLanguages = preSelectedOutputLanguages;
                }
            }
        },

        updateOutputsPace(state, action: PayloadAction<number | null>) {
            const pace = action.payload || 1.0; //always set a default value

            //update all outputs
            state.outputs = state.outputs?.map(output => {
                try {
                    getSessionContext()?.getText2Speech(output.language?.translationKey)?.setOutputPace(pace);
                } catch (error) {

                }
                return {...output, pace};
            });

            //save new value
            state.outputsPace = pace;
        },
        updateAutoDetectInoutLang(state, action: PayloadAction<boolean>) {
            state.autoDetectLang = action.payload;
        },

        updateSessionDuration(state, action: PayloadAction<number>) {
            let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

            state.sessionDurationInMins = action.payload;
            config = {...config, sessionDuration: action.payload};
            localStorage.setItem('preSelectedConfig', JSON.stringify(config));
        },

        updateBroadcastState(state, action: PayloadAction<boolean>) {
            state.broadcastEnabled = action.payload;
        },

        updateModelType(state, action: PayloadAction<string>) {
            let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

            state.modelType = action.payload;
            config = {...config, modelType: action.payload};
            localStorage.setItem('preSelectedConfig', JSON.stringify(config));
        },

        updateUseDictionary(state, action: PayloadAction<boolean>) {
            let config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

            state.useDictionary = action.payload;
            config = {...config, useDictionary: action.payload};
            localStorage.setItem('preSelectedConfig', JSON.stringify(config));
        },

        setGlossaryDisabled(state, action: PayloadAction<{id: string, disabled: boolean}>) {
            const {id, disabled} = action.payload;
            const disabledIds = new Set(state.disabledGlossaryIds);
            if (disabled) {
                disabledIds.add(id);
            } else {
                disabledIds.delete(id);
            }

            state.disabledGlossaryIds = Array.from(disabledIds);
            const config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
            localStorage.setItem('preSelectedConfig', JSON.stringify({
                ...config,
                disabledGlossaryIds: state.disabledGlossaryIds,
            }));
        },

        clearDisabledGlossaryIds(state) {
            state.disabledGlossaryIds = [];
            const config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
            localStorage.setItem('preSelectedConfig', JSON.stringify({
                ...config,
                disabledGlossaryIds: [],
            }));
        },

        pruneDisabledGlossaryIds(state, action: PayloadAction<string[]>) {
            const existingIds = new Set(action.payload);
            const prunedIds = state.disabledGlossaryIds.filter(id => existingIds.has(id));
            if (prunedIds.length === state.disabledGlossaryIds.length) {
                return;
            }

            state.disabledGlossaryIds = prunedIds;
            const config = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
            localStorage.setItem('preSelectedConfig', JSON.stringify({
                ...config,
                disabledGlossaryIds: prunedIds,
            }));
        },


        addPartiallyRecognizedText(state, action: PayloadAction<string>) {
            state.currentRecognition = action.payload;
            if (state.isTwoWayMode && state.activeInputPanelId) {
                state.currentRecognitionByPanel[state.activeInputPanelId] = action.payload;
                state.partialTextByPanel[state.activeInputPanelId] = action.payload;
            }
        },
        addRecognizedText(state, action: PayloadAction<Recognition>) {
            let recognitions = (state.recognizedTexts || [])
                .filter(recognition => {
                    return recognition.timestamp > (Date.now() - MAX_HISTORY_DURATION_IN_MILLIS);
                });
            recognitions.push(action.payload);

            state.recognizedTexts = recognitions;
            state.currentRecognition = null;

            if (state.isTwoWayMode && state.activeInputPanelId) {
                const panelId = state.activeInputPanelId;

                const panelRecognitions = (state.recognizedTextsByPanel[panelId] || [])
                    .filter(r => r.timestamp > (Date.now() - MAX_HISTORY_DURATION_IN_MILLIS));
                panelRecognitions.push(action.payload);
                state.recognizedTextsByPanel[panelId] = panelRecognitions;
                state.currentRecognitionByPanel[panelId] = null;

                // Unified panel history used by PanelTextHistory display component
                const history = (state.panelTextHistory[panelId] || [])
                    .filter(item => item.timestamp > (Date.now() - MAX_HISTORY_DURATION_IN_MILLIS));
                history.push({timestamp: action.payload.timestamp, text: action.payload.text});
                state.panelTextHistory[panelId] = history;
                state.partialTextByPanel[panelId] = null;
            }
        },
        addPartiallyTranslatedText(state, action: PayloadAction<Map<Language, TranslationResult>>) {
            let payload = action.payload;
            payload.forEach(((translationResult, lang) => {
                // console.log('updating reducer state: ', translationResult, lang);
                state.currentTranslations.set(lang.id, translationResult.translatedText);

                // Update partial text per panel for two-way mode
                if (state.isTwoWayMode) {
                    const panel = state.bidirectionalPanels.find(p => p.language?.translationKey === lang.id);
                    if (panel) {
                        state.partialTextByPanel[panel.id] = translationResult.translatedText;
                    }
                }
            }));
        },
        addTranslatedText(state, action: PayloadAction<TranslationsBody>) {
            let payload = action.payload;

            Object.keys(payload.outputs).forEach(((lang) => {
                if (!state.translations.has(lang))
                    state.translations.set(lang, [{timestamp: Date.now(), text: payload.outputs[lang]}]);
                else {
                    let translations = (state.translations.get(lang) || [])
                        .filter(translation => translation.timestamp > (Date.now() - MAX_HISTORY_DURATION_IN_MILLIS));
                    translations.push({timestamp: Date.now(), text: payload.outputs[lang]});

                    state.translations.set(lang, translations);
                }

                // Update unified panel history for two-way mode
                if (state.isTwoWayMode) {
                    const panel = state.bidirectionalPanels.find(p => p.language?.translationKey === lang);
                    if (panel) {
                        const history = (state.panelTextHistory[panel.id] || [])
                            .filter(item => item.timestamp > (Date.now() - MAX_HISTORY_DURATION_IN_MILLIS));
                        history.push({timestamp: Date.now(), text: payload.outputs[lang]});
                        state.panelTextHistory[panel.id] = history;
                        state.partialTextByPanel[panel.id] = null;
                    }
                }
            }));
            state.currentTranslations = new Map<string, string>();
        },

        toggleTwoWayMode(state, action: PayloadAction<boolean>) {
            state.isTwoWayMode = action.payload;
            const cfg = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
            localStorage.setItem('preSelectedConfig', JSON.stringify({...cfg, isTwoWayMode: action.payload}));
        },

        // Wipe all Two-Way state and its persisted config. Used when a user loses
        // access to the Two-Way feature so stale localStorage cannot leak back in
        // (e.g. the default isTwoWayMode/panel layout being restored on next load).
        clearTwoWayConfig(state) {
            state.isTwoWayMode = false;
            state.bidirectionalPanels = loadBidirectionalPanelsFromCache({} as Config);
            state.activeInputPanelId = 'bipanel_0';
            state.floorFeedEnabled = false;

            const cfg = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
            delete cfg.isTwoWayMode;
            delete cfg.bidirectionalPanelsConfig;
            delete cfg.activeInputPanelId;
            delete cfg.floorFeedEnabled;
            localStorage.setItem('preSelectedConfig', JSON.stringify(cfg));
        },

        addBiPanel(state, action: PayloadAction<void>) {
            const usedColors = new Set(state.bidirectionalPanels.map(p => p.color));
            const color = BIDIRECTIONAL_PANEL_COLORS.find(c => !usedColors.has(c))
                ?? BIDIRECTIONAL_PANEL_COLORS[state.bidirectionalPanels.length % BIDIRECTIONAL_PANEL_COLORS.length];
            const newId = 'bipanel_' + Math.floor(Math.random() * 100000);
            state.bidirectionalPanels.push({
                id: newId, language: null, inputDevice: null, outputDevice: null,
                inputGain: 0.5, outputGain: 1.0, voiceName: null, textOnly: false, outputOnly: false, inputOnly: false, color,
            });
            saveBiPanelConfig(state);
        },

        removeBiPanel(state, action: PayloadAction<string>) {
            const panelId = action.payload;
            state.bidirectionalPanels = state.bidirectionalPanels.filter(p => p.id !== panelId);
            if (state.activeInputPanelId === panelId) {
                state.activeInputPanelId = state.bidirectionalPanels[0]?.id ?? null;
            }
            // output-only / input-only modes require 3+ panels; clear both if we're back below that
            if (state.bidirectionalPanels.length < 3) {
                state.bidirectionalPanels.forEach(p => { p.outputOnly = false; p.inputOnly = false; });
                const activeIsValid = state.bidirectionalPanels.some(p => p.id === state.activeInputPanelId);
                if (!activeIsValid) state.activeInputPanelId = state.bidirectionalPanels[0]?.id ?? null;
            }
            saveBiPanelConfig(state);
            recomputeInputOutputs(state);
        },

        updateBiPanel(state, action: PayloadAction<{ panelId: string, changes: Partial<BidirectionalPanel> }>) {
            const {panelId, changes} = action.payload;
            const idx = state.bidirectionalPanels.findIndex(p => p.id === panelId);
            if (idx !== -1) {
                state.bidirectionalPanels[idx] = {...state.bidirectionalPanels[idx], ...changes};
                // If this panel just became output-only and was the active input, move active to another panel
                if (state.bidirectionalPanels[idx].outputOnly && panelId === state.activeInputPanelId) {
                    const firstValid = state.bidirectionalPanels.find(p => p.id !== panelId && !p.outputOnly);
                    if (firstValid) state.activeInputPanelId = firstValid.id;
                }
            }
            saveBiPanelConfig(state);
            recomputeInputOutputs(state);

            // Keep two-way output pinned to the selected device:
            // if that selected device is disconnected, mute this panel's TTS output
            // instead of letting browser/OS reroute audio to a fallback device.
            if (changes.outputDevice !== undefined && idx !== -1) {
                const updatedPanel = state.bidirectionalPanels[idx];
                const translationKey = updatedPanel.language?.translationKey;
                if (translationKey && panelId !== state.activeInputPanelId) {
                    try {
                        if (isOutputDeviceActive(changes.outputDevice)) {
                            getSessionContext()?.setOutputDevice(translationKey, changes.outputDevice as MediaDeviceInfo, updatedPanel.outputGain, true);
                        }
                    } catch (_e) {}
                    applyLiveOutputVolume(translationKey, changes.outputDevice, updatedPanel.outputGain);
                }
            }

            if (changes.outputGain !== undefined && idx !== -1) {
                const updatedPanel = state.bidirectionalPanels[idx];
                const translationKey = updatedPanel.language?.translationKey;
                if (translationKey && panelId !== state.activeInputPanelId) {
                    // Mutes while the selected device is disconnected; prevents
                    // browser/OS fallback routing to the default speakers.
                    applyLiveOutputVolume(translationKey, updatedPanel.outputDevice, changes.outputGain);
                }
            }
        },

        setActiveInputPanel(state, action: PayloadAction<string>) {
            const newPanelId = action.payload;
            state.activeInputPanelId = newPanelId;
            const cfg = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
            localStorage.setItem('preSelectedConfig', JSON.stringify({...cfg, activeInputPanelId: newPanelId}));
            recomputeInputOutputs(state);
        },

        toggleFloorFeed(state, action: PayloadAction<boolean>) {
            state.floorFeedEnabled = action.payload;
            const cfg = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
            localStorage.setItem('preSelectedConfig', JSON.stringify({...cfg, floorFeedEnabled: action.payload}));
        },

        clearRecognitionHistory(state) {
            state.recognizedTexts = [];
            state.currentRecognition = null;
            state.recognizedTextsByPanel = {};
            state.currentRecognitionByPanel = {};
            state.panelTextHistory = {};
            state.partialTextByPanel = {};
            state.translations = new Map<string, { timestamp: number, text: string }[]>();
            state.currentTranslations = new Map<string, string>();
        },

        //==== MOCKS ====
        addMockRecognizedText(state, action: PayloadAction<Recognition>) {
            const recognizedText = action.payload;
            state.recognizedTexts.push(recognizedText)
        },
        addMockTranslations(state, action: PayloadAction<number>) {
            const recognizedTextId = action.payload;

            const recognition = state.recognizedTexts.find(recognizedT => recognizedT.id === recognizedTextId);
            if (!recognition) return;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(validateAndStartSession.pending, (state) => {
            // console.log('startSession.pending!')
        })
        builder.addCase(validateAndStartSession.fulfilled, (state, {payload}) => {
            // console.log("fin 0.5", payload)
            state.currentSessionId = payload.sessionId;
            state.currentSessionAPIToken = payload.sessionSpeechToken;
            // console.log('startSession.fulfilled!', payload, state.currentSessionId)
        })
        builder.addCase(validateAndStartSession.rejected, (state, {payload}) => {
            // console.log('startSession.rejected!', payload)
        })

        builder.addCase(stopCurrentSession.pending, (state) => {
            // console.log('stopCurrentSession.pending!')
        })
        builder.addCase(stopCurrentSession.fulfilled, (state, action) => {
            // A late stop response for an older session must not clear a newer session.
            if (state.currentSessionId === action.meta.arg.sessionId) {
                state.currentSessionId = null;
                state.currentSessionAPIToken = null;
            }
            // console.log('stopCurrentSession.fulfilled!', payload)
        })
        builder.addCase(stopCurrentSession.rejected, (state, {payload}) => {
            // console.log('stopCurrentSession.rejected!', payload)
        })

        builder.addCase(sendTranslations.pending, (state) => {
            // console.log('sendTranslations.pending!')
        })
        builder.addCase(sendTranslations.fulfilled, (state, {payload}) => {
            // console.log('sendTranslations.fulfilled!', payload)
        })
        builder.addCase(sendTranslations.rejected, (state, {payload}) => {
            // console.log('sendTranslations.rejected!', payload)
        })

        builder.addCase(updateSession.pending, (state) => {
            // console.log('updateSession.pending!')
        })
        builder.addCase(updateSession.fulfilled, (state, {payload}) => {
            // console.log('updateSession.fulfilled!', payload)
        })
        builder.addCase(updateSession.rejected, (state, {payload}) => {
            // console.log('updateSession.rejected!', payload)
        })
    }
});

export default sessionSlice.reducer;
export const {
    setApiKey,
    updateCanStartSession,
    updateSessionState,
    setInitialOutputDevicesAmount,

    updateOutputsPace,
    updateAutoDetectInoutLang,
    updateSessionDuration,
    updateBroadcastState,

    updateModelType,
    updateUseDictionary,
    setGlossaryDisabled,
    clearDisabledGlossaryIds,
    pruneDisabledGlossaryIds,

    addPanel,
    removePanel,
    updatePanelConfiguration,

    addPartiallyRecognizedText,
    addRecognizedText,
    addPartiallyTranslatedText,
    addTranslatedText,

    addMockRecognizedText,
    addMockTranslations,

    toggleTwoWayMode,
    clearTwoWayConfig,
    addBiPanel,
    removeBiPanel,
    updateBiPanel,
    setActiveInputPanel,
    toggleFloorFeed,
    clearRecognitionHistory,
} = sessionSlice.actions;
